/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MonsterSelector from 'components/search/MonsterSelector';
import MonsterDisplay from './MonsterDisplay';
import PreferenceService from './services/PreferenceService';
import DownloadJsonButton from 'components/export/DownloadJsonButton';
import DownloadAllMonstersButton from 'components/export/DownloadAllMonstersButton';
import ExportAllToVttButton from 'components/export/ExportAllToVttButton';
import DivToImage from 'components/export/DivToImage';
import DataLoader from 'components/common/DataLoader';
import Open5e from 'model/formats/Open5e';
import TetraCube from 'model/formats/TetraCube';
import CacheBlock from 'components/common/CacheBlock';
import saveImg from './dndimages/save-icon.png';
import deleteImg from './dndimages/delete-icon.png';
import pnNpcImg from './dndimages/wand-icon.png';
import logoTrimmedImg from './dndimages/LogoTrimmed.png';
import MonsterSearch from 'components/search/MonsterSearch';
import CharacterModal from './character/CharacterModal';
import MessageModal from './character/MessageModal';
import MonsterUnavailableModal from 'components/sharing/MonsterUnavailableModal';
import ShareMonsterModal from 'components/sharing/ShareMonsterModal';
import ReportMonsterModal from 'components/sharing/ReportMonsterModal';
import RejectMonsterModal from 'components/sharing/RejectMonsterModal';
import ViolationResponseModal from 'components/sharing/ViolationResponseModal';
import MyViolationsModal from 'components/sharing/MyViolationsModal';
import MyFlaggedMonstersModal from 'components/sharing/MyFlaggedMonstersModal';
import Monster from 'model/monster/Monster';
import { hasOutstandingViolations, isAwaitingApproval } from 'model/Visibility';
import Spell from 'model/stats/Spell';
import NpcData from 'model/monster/NpcData';
import DataModel from 'model/DataModel';
import Login from './auth/Login';
import { auth, firestoreService } from './services/FirestoreService';
import { onAuthStateChanged, User } from 'firebase/auth';
import ObjectHelper from 'utils/ObjectHelper';
import PrivacyNotice from './auth/PrivacyNotice';
import SubscriptionRenewalModal from './auth/SubscriptionRenewalModal';
import TourMain from './tour/TourMain';
import TourGridSearch from './tour/TourGridSearch';
import TourMM2024 from './tour/TourMM2024';
import TourSpells from './tour/TourSpells';
import TourSelector, { TourId } from './tour/TourSelector';
import ResumeHint from './tour/ResumeHint';
import PropertySet from 'components/common/PropertySet';
import DownloadVttFormatButton from 'components/export/DownloadVttFormatButton';
import PrintViewButton, { PrintMode } from 'components/export/PrintViewButton';
import { SubscriptionTier } from './services/StripeService';
import { fetchImageAsDataUrl, convertToWebP, scaleDownImage, MAX_IMAGE_SIZE } from './utils/ImageUtils';
import CreatureOpen5e from 'model/formats/CreatureOpen5e';
import MonsterOpen5e from 'model/formats/MonsterOpen5e';
import ApiService from './services/ApiService';
import { CLOUD_LAYER_DEFS } from 'constants/CloudLayers';
import { collectStyleSheets, buildMonsterBlockHtml, buildSeparatorHtml, buildPrintDocumentHtml } from 'utils/PrintViewGenerator';

const MonsterContainer: React.FC = () => {
  // Populated when reached via /monsterforge/mon/:username/:monstername (the public share URL);
  // both are undefined on the plain /monsterforge editor route.
  const { username, monstername, monsterKey } = useParams<{ username?: string; monstername?: string; monsterKey?: string }>();
  const navigate = useNavigate();
  const exportImageDivSource = useRef<HTMLDivElement>(null);
  const [showAll, setShowAll] = useState<boolean>(true);

  const [showCache, setShowCache] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showAccount, setShowAccount] = useState<boolean>(false);
  const [mon, setMon] = useState<Monster>(new Monster());
  const [showWarnMessage, setShowWarnMessage] = useState<boolean>(true);
  const [isWarnMessageOpen, setWarnMessageOpen] = useState<boolean>(false);
  const [isPcModalOpen, setPcModalOpen] = useState<boolean>(false);
  const [searchMinCr, setSearchMinCr] = useState<number | null>(null);
  const [searchMaxCr, setSearchMaxCr] = useState<number | null>(null);
  const [searchKeywords, setSearchKeywords] = useState<string>('');
  const [searchPageSize, setSearchPageSize] = useState<number | null>(null);
  const [searchCurrentPage, setSearchCurrentPage] = useState<number | null>(null);
  const [searchSortBy, setSearchSortBy] = useState<{ id: string; desc: boolean }[]>([]);
  const [spellsByKey, setSpellsByKey] = useState<Map<string, Spell>>(new Map());
  const spellsByKeyRef = useRef<Map<string, Spell>>(new Map());
  // Guards the once-per-login "you have monsters with open violations" notification (see the effect
  // below). Reset on logout so a fresh login re-checks.
  const loginViolationCheckRef = useRef<boolean>(false);
  // True while a visibility change is being persisted, to ignore re-entrant clicks (the UI updates
  // optimistically, so without this a quick second click could race overlapping server writes).
  const visibilityChangingRef = useRef<boolean>(false);
  // The catalog deep-link (/monsterforge/cat/:monsterKey) key already loaded this session, so the
  // effect doesn't re-load it (e.g. once the full catalog arrives after a single-monster fetch).
  // Reset when the route leaves the cat link, so revisiting the same key in-session works.
  const catLinkHandledKeyRef = useRef<string>('');
  // The print-view tab opened by the Print View button. While this tab is still open, subsequent
  // Print View clicks re-render into it rather than spawning a new tab. (A future dropdown will let
  // the user choose append-to-existing vs. always-new-tab; for now we always reuse if it's open.)
  const printWindowRef = useRef<Window | null>(null);
  const [dataModel] = useState<DataModel>(new DataModel());
  const [searchEnvironments, setSearchEnvironments] = useState<string[]>([]);
  const [, setForceUpdate] = useState(0); // Dummy state to force re-renders
  const [myMonsters, setMyMonsters] = useState<Monster[]>([]);
  // Other users' public (approved + clean) monsters, fetched on login. Shown in the search grid but
  // kept separate from myMonsters (the user doesn't own them). Paired with the owner's username so
  // the grid can show it as the monster's source.
  const [communityMonsters, setCommunityMonsters] = useState<{ username: string; monster: Monster }[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<Record<string, any> | null>(null);
  const [monsterLimit, setMonsterLimit] = useState<number>(0);
  const [environments, setEnvironments] = useState<string[]>(['Water', 'Mountains', 'Desert', 'Hills']);
  const [showPrivacy, setShowPrivacy] = useState<boolean>(false);
  const [selectedMonsterId, setSelectedMonsterId] = useState<string>('');
  const [tourStep, setTourStep] = useState<number>(0);
  const [showTour, setShowTour] = useState<boolean>(false);
  const [showGridTour, setShowGridTour] = useState<boolean>(false);
  const [gridTourStep, setGridTourStep] = useState<number>(0);
  const [showMm2024Tour, setShowMm2024Tour] = useState<boolean>(false);
  const [mm2024TourStep, setMm2024TourStep] = useState<number>(0);
  const [showTourSelector, setShowTourSelector] = useState<boolean>(false);
  const [showSpellsTour, setShowSpellsTour] = useState<boolean>(false);
  const [spellsTourStep, setSpellsTourStep] = useState<number>(0);
  const [showResumeHint, setShowResumeHint] = useState<boolean>(false);
  // The "how to get back to the tours" hint is shown once ever — when the user closes the LAST tour
  // surface (a tour they didn't return to the selector from, or the selector itself). 'seen' lives in a
  // ref loaded from IndexedDB so the (synchronous) close handlers can decide without awaiting.
  const tourHintSeenRef = useRef<boolean>(false);
  useEffect(() => {
    PreferenceService.getUserPreference('tourCloseHintSeen').then((v) => { tourHintSeenRef.current = v === 'true'; });
  }, []);
  const maybeShowResumeHint = () => {
    if (!tourHintSeenRef.current) {
      tourHintSeenRef.current = true;
      PreferenceService.setUserPreference('tourCloseHintSeen', 'true');
      setShowResumeHint(true);
    }
  };

  // The main '?' button opens the tour selector; choosing an entry launches that tour. The grid-search
  // tour highlights the open search modal, so we open that modal too.
  const handleSelectTour = (tour: TourId) => {
    setShowTourSelector(false);
    switch (tour) {
      case 'main':   setTourStep(1); setSearchEnvironments([]); setSearchMinCr(null); setSearchMaxCr(null); setShowTour(true); break;
      case 'search': setSearchKeywords(''); setSearchEnvironments([]); setSearchMaxCr(null); setSearchMinCr(null); setGridTourStep(0); setShowGridTour(true); break;
      case 'spells': setSpellsTourStep(0); setShowSpellsTour(true); break;
      case 'mm2024': setMm2024TourStep(0); setShowMm2024Tour(true); break;
    }
  };
  const [openIndex, setOpenIndex] = useState<number>(-1);
  const [openBlock, setOpenBlock] = useState<string>('');
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [envOpen, setEnvOpen] = useState<boolean>(false);
  const [spellbookOpen, setSpellbookOpen] = useState<boolean>(false);
  const [tourState, setTourState] = useState<string>('');
  const [watchingUser, setWatchingUser] = useState<boolean>(false);
  const [showRenewalModal, setShowRenewalModal] = useState<boolean>(false);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState<boolean>(false);
  const [currentMonsterHash, setCurrentMonsterHash] = useState<string>('');
  const [savedMonsterHash, setSavedMonsterHash] = useState<string>('');
  const [monsterVersion, setMonsterVersion] = useState<number>(0);
  // Set when a public share URL points at a monster that can no longer be fetched as public.
  const [showUnavailableModal, setShowUnavailableModal] = useState<boolean>(false);
  // Controls the Share modal (Private / Shared / Public chooser).
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  // Set when a share request is rejected (e.g. the monster contains profanity); shown in the modal.
  const [shareError, setShareError] = useState<string>('');
  // Set when the visitor opens the "Report Inappropriate" form for someone else's monster.
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  // Set when an approver opens the reject-reason form for someone else's monster.
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  // True while an Approve/Reject moderation call is in flight, to disable both buttons until it resolves.
  const [moderationBusy, setModerationBusy] = useState<boolean>(false);
  // Set when an approver opens the violation-response form for a reported monster.
  const [showViolationModal, setShowViolationModal] = useState<boolean>(false);
  // Set when the owner opens the "violations against my monster" view.
  const [showMyViolationsModal, setShowMyViolationsModal] = useState<boolean>(false);
  // Set when the owner opens their personal queue of monsters that have outstanding violations.
  const [showMyFlaggedModal, setShowMyFlaggedModal] = useState<boolean>(false);

  // Generate a hash code for a monster to detect changes
  const generateMonsterHash = (monster: Monster): string => {
    try {
      const data: any = prepMonsterForSave(monster);
      const cleanedData: any = ObjectHelper.removeEmpty(data);
      const sortedData = ObjectHelper.sortObjectKeys(cleanedData);
      const jsonString = JSON.stringify(sortedData);

      // Simple hash function
      let hash = 0;
      for (let i = 0; i < jsonString.length; i++) {
        const char = jsonString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return hash.toString();
    } catch (error) {
      console.error('Error generating monster hash:', error);
      return '';
    }
  };

  const updateEnvironments = () => {
    const allEnvs: Set<string> = new Set(dataModel.monsters.data.flatMap(mon => mon.environments || []));
    // get all the environments from the 2024 creatures:
    for (const creature of dataModel.creatures.data) {
      for (const env of creature.environments) {
        if (typeof(env) === 'string') {
          allEnvs.add(env);
        } else {
          allEnvs.add(env.name);
        }
      }
    }
    setEnvironments(Array.from(allEnvs).sort());
  }
  useEffect(() => {
    updateEnvironments();
  }, [dataModel.monsters.data.length, dataModel.creatures.data.length]);

  // Update current monster hash whenever the monster changes
  useEffect(() => {
    const hash = generateMonsterHash(mon);
    setCurrentMonsterHash(hash);
  }, [mon, monsterVersion]);

  // Keep the open monster's moderation state in sync with its authoritative saved copy. These fields
  // change outside the editor (a moderator approving / rejecting, or anyone reporting) — including
  // desiredVisibility, which a violation/copyright report forces back to Private so the owner can
  // remediate. When the My Monsters snapshot reflects new values for the monster currently being
  // viewed, mirror them onto the open copy. Triggered only by the snapshot (not by local edits), and
  // only for the user's own saved monster (matched by ownerId). Treated as clean. (Safe to sync
  // desiredVisibility because owner-initiated visibility changes are persisted immediately, so the
  // local value never legitimately leads the saved one.)
  useEffect(() => {
    if (!user || !mon?.name || mon.ownerId !== user.uid) {
      return;
    }
    const saved = myMonsters.find(m => m.name === mon.name);
    if (!saved) {
      return;
    }
    const approvalChanged = JSON.stringify(saved.approval || null) !== JSON.stringify(mon.approval || null);
    const summaryChanged = JSON.stringify(saved.violationSummary || {}) !== JSON.stringify(mon.violationSummary || {});
    const flaggedChanged = (saved.flaggedForReview || false) !== (mon.flaggedForReview || false);
    const desiredChanged = (saved.desiredVisibility || 'Private') !== (mon.desiredVisibility || 'Private');
    const errorCountChanged = (saved.errorReportCount || 0) !== (mon.errorReportCount || 0);
    if (approvalChanged || summaryChanged || flaggedChanged || desiredChanged || errorCountChanged) {
      let synced = Monster.updateMonster(mon, 'approval', saved.approval || null, false);
      synced = Monster.updateMonster(synced, 'violationSummary', saved.violationSummary || { open: 0, confirmed: 0 }, false);
      synced = Monster.updateMonster(synced, 'flaggedForReview', saved.flaggedForReview || false, false);
      synced = Monster.updateMonster(synced, 'desiredVisibility', saved.desiredVisibility || 'Private', false);
      synced = Monster.updateMonster(synced, 'errorReportCount', saved.errorReportCount || 0, false);
      const hash = generateMonsterHash(synced);
      setMon(synced);
      setCurrentMonsterHash(hash);
      setSavedMonsterHash(hash);
    }
  }, [myMonsters]);

  // Once per login, after the user's monsters have loaded, notify them if any of their monsters has
  // outstanding violations by opening their violations queue. Guarded by a ref so it fires only on
  // the first populated snapshot (not again when they resolve a report, refresh a token, etc.). A
  // user with violations necessarily has monsters, so an empty list just means "not loaded yet".
  useEffect(() => {
    if (!user || loginViolationCheckRef.current || myMonsters.length === 0) {
      return;
    }
    loginViolationCheckRef.current = true;
    if (myMonsters.some(m => hasOutstandingViolations(m.violationSummary) || (m.errorReportCount || 0) > 0)) {
      setShowMyFlaggedModal(true);
    }
  }, [myMonsters, user]);

  useEffect(() => {
    // Watch for auth state changes
    onAuthStateChanged(auth, (user: User | null) => {
      setUser(user);
      // The community list is public (the getPublicMonsters callable needs no auth), so load it either
      // way — logged-out visitors can browse GGG Community monsters too.
      loadCommunityMonsters();
      if (user) {
        watchUserMonsters();
        const watching = firestoreService.watchUserData(setUserData);
        setWatchingUser(watching);
      } else {
        setMyMonsters([]);
        loginViolationCheckRef.current = false; // re-check on the next login
      }
    });

    // See if we need to show the tour for first-time users. Skip it entirely when the user arrived
    // via a direct monster link (/mon/:username/:monstername or /cat/:monsterKey): don't auto-start
    // the tour, and don't persist any tour status (only onCloseTour writes it, and that can't run if
    // the tour never shows) — so the tour still starts on their next, normal visit.
    const getTourState = async() => {
      if ((username && monstername) || monsterKey) {
        return;
      }
      const showTour = await PreferenceService.getUserPreference("showTour");
      if (showTour !== 'false') {
        setShowTour(true);
      }
    }
    getTourState();

    // Load the last monster from local storage when the component mounts, unless we arrived via a
    // public share URL (handled by the effect below), in which case that monster takes precedence.
    const lastMonster = localStorage.getItem('currentMonster');
    const lastShowAll = localStorage.getItem('showAll');
    if (lastMonster && !(username && monstername) && !monsterKey) {
      const rawObj = JSON.parse(lastMonster);
      const monster = Monster.convertFromJson(rawObj);
      setMon(monster);
      // Restore which monster this is (catalog slug, 'user_…', or 'community_…') so the identity-derived
      // controls (Share / Report / View reports) reappear — otherwise the restored monster looks like an
      // unsaved draft. e.g. returning from the moderation page to a catalog monster.
      const lastMonsterId = localStorage.getItem('currentMonsterId');
      if (lastMonsterId) {
        setSelectedMonsterId(lastMonsterId);
      }
    }
    if (lastShowAll != null) {
      setShowAll(lastShowAll === "true");
    }

    const onDataModelUpdate = () => {
      setForceUpdate((prev) => prev + 1); // force a redraw
      const newSpellsByKey = new Map<string, Spell>();
      for (const clss of dataModel.spellsBySpellLevelByClass.keys()) {
        const classSpells = dataModel.spellsBySpellLevelByClass.get(clss);
        for (const level of classSpells?.keys() || []) {
          const levelSpells = classSpells?.get(level);
          for (const spell of levelSpells || []) {
            newSpellsByKey.set(spell.key, spell);
          }
        }
      }
      setSpellsByKey(newSpellsByKey);
    };

    // First check for the cached data:
    dataModel.fetchAll(true);
    // And then check for non-cached data, if anything is missing:
    dataModel.fetchAll(false, updateEnvironments);

    // Cleanup on unmount
    return dataModel.subscribe(onDataModelUpdate);

  }, []);

  // Reached via /monsterforge/cat/:monsterKey (a share/moderation link to a built-in catalog monster,
  // which has no owner so it can't go through getPublicMonster). If the catalog is already loaded, pick
  // the monster straight from it. Otherwise — e.g. a brand-new visitor following the link before the
  // catalog has finished loading — fetch just this one monster directly from open5e by its key so it
  // shows immediately; the full catalog keeps loading in the background. The guard ref stops a redundant
  // reload when the background catalog load completes after the single fetch already showed the monster.
  useEffect(() => {
    if (!monsterKey) {
      catLinkHandledKeyRef.current = ''; // left the cat link; allow revisiting the same key later
      return;
    }
    if (catLinkHandledKeyRef.current === monsterKey) {
      return;
    }

    const loadOpen5eMonster = (open5eMon: MonsterOpen5e) => {
      catLinkHandledKeyRef.current = monsterKey;
      const newMon = Open5e.translateMonster(structuredClone(open5eMon));
      Monster.validate(newMon);
      setMon(newMon);
      setMonsterVersion(0);
      setSelectedMonsterId(monsterKey);
      setShowAll(false);
    };

    const fromCatalog = findCatalogMonster(monsterKey);
    if (fromCatalog) {
      loadOpen5eMonster(fromCatalog);
      return;
    }

    // Catalog (or at least this monster) not loaded yet; fetch the single monster from open5e.
    let cancelled = false;
    ApiService.getMonsterByKey(monsterKey).then(open5eMon => {
      // Bail if unmounted/route changed, or the background catalog load already handled this key.
      if (cancelled || catLinkHandledKeyRef.current === monsterKey || !open5eMon) {
        return;
      }
      loadOpen5eMonster(open5eMon);
    });
    return () => { cancelled = true; };
  }, [monsterKey, dataModel.monsters.data.length, dataModel.creatures.data.length]);

  // When reached via /monsterforge/mon/:username/:monstername, load that user's public monster.
  // It comes back read-only because its ownerId won't match the current visitor (or there is
  // no visitor). Spell objects are rehydrated from their stored keys if the spell DB is ready.
  useEffect(() => {
    if (!username || !monstername) {
      return;
    }
    let cancelled = false;
    const loadPublicMonster = async () => {
      const { monster: fetched } = await firestoreService.getPublicMonster(username, monstername);
      if (cancelled) {
        return;
      }
      if (!fetched) {
        // The monster is gone or no longer public: fall back to the visitor's default monster
        // (their last-edited one, or a fresh one) and tell them the link is no longer visible.
        const lastMonster = localStorage.getItem('currentMonster');
        try {
          setMon(lastMonster ? Monster.convertFromJson(JSON.parse(lastMonster)) : new Monster());
        } catch {
          setMon(new Monster());
        }
        setMonsterVersion(0);
        setShowUnavailableModal(true);
        return;
      }
      if (fetched.npcData) {
        try {
          NpcData.updateSpellsFromKeys(fetched.npcData, spellsByKeyRef.current);
        } catch (e) {
          console.error('Error updating spells for public monster:', e);
        }
      }
      Monster.validate(fetched);
      setMon(fetched);
      setMonsterVersion(0);
      setShowAll(false);
    };
    loadPublicMonster();
    return () => { cancelled = true; };
  }, [username, monstername]);

  const onUserChanged = (newUser: User | null) => {
    // Don't call setUserData(newUser) - newUser is a Firebase User object, not Firestore user data
    // The Firestore user data will be set correctly by watchUserData below
    if (!watchingUser) {
      setTimeout(() => setWatchingUser(firestoreService.watchUserData(setUserData)), 100);
    }
  }

  const onCloseTour = (completed?: boolean) => {
    setShowTour(false);
    PreferenceService.setUserPreference('showTour', 'false');
    if (completed) {
      setShowTourSelector(true);   // finished the tour -> back to the selector to pick another
    } else {
      maybeShowResumeHint();       // closed early, not returning to the selector -> this was the last surface
    }
  }

  // Save the current monster to local storage whenever it changes. Skip this for a public
  // share view (/monsterforge/mon/:username/:monstername) so it never clobbers the visitor's own
  // last-edited monster, and so the plain editor doesn't reopen on someone else's monster.
  useEffect(() => {
    if (mon && !(username && monstername)) {
      const monsterString = JSON.stringify(mon);
      localStorage.setItem('currentMonster', monsterString);
    }
  }, [mon, mon?.npcData]);

  // Persist the selected monster's identity alongside currentMonster, so it can be restored on the
  // next mount (see the restore in the mount effect). Skipped for /mon/ share views, matching above.
  useEffect(() => {
    if (!(username && monstername)) {
      localStorage.setItem('currentMonsterId', selectedMonsterId);
    }
  }, [selectedMonsterId, username, monstername]);
  
  useEffect(() => {
    localStorage.setItem('showAll', showAll.toString());
  }, [showAll]);

  useEffect(() => {
    localStorage.setItem('showWarnMessage', showWarnMessage.toString());
  }, [showWarnMessage]);

  useEffect(() => {
    let subscriptionExpired = false;
    let monsterLimit = 0;
    if (userData) {
      // check if subscription is expired
      const expirationDate = userData['subscriptionExpirationDate'];
      if (expirationDate) {
        // Convert Firestore Timestamp to Date if needed
        const expirationDateObj = expirationDate.toDate ? expirationDate.toDate() : new Date(expirationDate);
        subscriptionExpired = expirationDateObj < new Date();
      }
      if (!subscriptionExpired) {
        monsterLimit = userData['monsterLimit'] || 0;
      }
      setIsSubscriptionExpired(subscriptionExpired);
    }
    setMonsterLimit(monsterLimit);
  }, [userData]);

  const handleUpdateMonster = (field: string, value: any) => {
    setMon(prevMon => Monster.updateMonster(prevMon, field, value, false));
    // Increment version to trigger hash recalculation
    setMonsterVersion(prev => prev + 1);
  };

  // Force hash recalculation without updating monster
  const handleMonsterMutated = () => {
    setMonsterVersion(prev => prev + 1);
  };

  // Make an editable copy of a monster the visitor is viewing read-only (someone else's public
  // monster). The copy becomes the visitor's own Private monster so read-only mode lifts and they
  // can edit, export, or save it to their collection. We leave the public share URL and persist
  // the copy to local storage so the normal editor picks it up after navigation.
  // Pick a name for a copied monster that doesn't collide with one already in the user's collection.
  // An unused name is kept as-is; a collision becomes "<name> (copy)", then "(copy2)", "(copy3)", …
  const uniqueCopyName = (baseName: string): string => {
    const taken = new Set(myMonsters.map(m => m.name));
    if (!taken.has(baseName)) {
      return baseName;
    }
    let candidate = `${baseName} (copy)`;
    let n = 2;
    while (taken.has(candidate)) {
      candidate = `${baseName} (copy${n})`;
      n++;
    }
    return candidate;
  };

  // Add another user's monster to YOUR collection (the Add/Copy button when viewing it). Copies it,
  // claiming ownership and resetting the moderator-managed fields to defaults, and — if you already
  // have a monster with that name — renames it ' (copy)' so it doesn't overwrite yours, then saves.
  // Same end result as adding a monster from the catalog.
  const handleAddOtherUsersMonster = async () => {
    if (!mon) {
      return;
    }
    const plain = JSON.parse(JSON.stringify(mon));
    plain.ownerId = user?.uid || '';
    plain.desiredVisibility = 'Private';
    plain.approval = null;
    plain.violationSummary = { open: 0, confirmed: 0 };
    plain.flaggedForReview = false;
    if (myMonsters?.some(m => m.name === mon.name)) {
      plain.name = uniqueCopyName((plain.name || '').toString());
    }
    const copy = Monster.convertFromJson(plain);
    clearShareUrl();
    setMon(copy);
    setMonsterVersion(0);
    // Duplicate the image so the copy doesn't depend on the original owner's Storage object (which is
    // deleted when they delete their monster). Fetch their image as a data URL; persistMonster then
    // re-uploads it under THIS user's Storage path. If the fetch fails, fall back to the original URL.
    if (copy.image?.src && (copy.image.src.startsWith('http://') || copy.image.src.startsWith('https://'))) {
      try {
        const dataUrl = await fetchImageAsDataUrl(copy.image.src);
        if (dataUrl) {
          copy.image.src = dataUrl;
        }
      } catch (e) {
        console.error('Could not duplicate the monster image; the copy will reference the original.', e);
      }
    }
    const ok = await persistMonster(copy);
    if (ok) {
      setSelectedMonsterId('user_' + copy.name);
      const hash = generateMonsterHash(copy);
      setCurrentMonsterHash(hash);
      setSavedMonsterHash(hash);
    }
  };

  // Submit a report of someone else's monster as inappropriate (from the public view). The server
  // records it in the 'violations' collection and, if the monster is 'Pending', hides it.
  // The owner's username comes from the share-URL params when viewing via a link; when the monster
  // was opened from the community grid/selector there are no params, so fall back to the username
  // paired with this community monster (matched by owner + name).
  const reportedOwnerUsername = (): string =>
    username
    || communityMonsters.find(c => c.monster.ownerId === mon.ownerId && c.monster.name === mon.name)?.username
    || '';

  // The public share link for the monster being reported. window.location.href is only the public
  // URL when reached via a share link; when opened from the grid/selector it's just '/monsterforge#',
  // so build the canonical link from the owner's username and the monster name instead.
  const reportedMonsterUrl = (): string => {
    const owner = reportedOwnerUsername();
    const name = monstername || mon.name;
    return owner
      ? `${window.location.origin}/monsterforge/mon/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`
      : window.location.href;
  };

  const submitReport = async (report: {
    reportType: 'violation' | 'error' | 'copyright';
    reporterName: string;
    reporterEmail: string;
    offensiveType?: 'text' | 'image' | 'both';
    description: string;
  }): Promise<boolean> => {
    const ownerUname = reportedOwnerUsername();
    return firestoreService.reportMonster({
      username: ownerUname,
      monsterName: monstername || mon.name,
      url: reportedMonsterUrl(),
      // For a catalog monster (no owner) include its unique data-model key so the moderation queue can
      // link back to it via /monsterforge/cat/:monsterKey, plus its document source for display there.
      monsterKey: ownerUname ? '' : selectedMonsterId,
      monsterSource: ownerUname ? '' : (mon.doc_ref?.document__title || mon.doc_ref?.document__slug || ''),
      ...report,
    });
  };

  // Approver moderation actions on another user's monster being viewed. The Firestore document id
  // is the monster name with '/' replaced (mirrors FirestoreService.prepMonsterName).
  const moderationMonsterId = () => mon.name.trim().replace(/\//g, '_');

  const handleApprove = async () => {
    if (moderationBusy) {
      return;
    }
    setModerationBusy(true);
    const ok = await firestoreService.approveMonster(mon.ownerId, moderationMonsterId());
    if (ok) {
      // Reflect the approval locally so the Approve/Reject buttons immediately give way to the
      // normal Copy/Report controls. The monster was loaded one-time via getPublicMonster, so there
      // is no snapshot to refresh it; without this the buttons would persist until a reload.
      handleUpdateMonster('approval', { level: mon.desiredVisibility, by: user?.uid || '', at: Date.now() });
    }
    setModerationBusy(false);
  };

  const submitReject = async (reason: string): Promise<boolean> => {
    setModerationBusy(true);
    const ok = await firestoreService.rejectMonster(mon.ownerId, moderationMonsterId(), reason);
    if (ok) {
      setShowRejectModal(false);
      navigate('/monsterforge/moderation');
    }
    setModerationBusy(false);
    return ok;
  };

  // Moderator dismisses a single report (the monster is already Private from the report). Stays in the
  // modal, which reloads its list — like the owner resolving their own violations one at a time.
  const dismissOneReport = async (violationId: string): Promise<boolean> => {
    return firestoreService.respondToViolation(mon.ownerId, moderationMonsterId(), 'dismiss', violationId);
  };

  // Moderator deletes the reported monster outright. On success, close the modal and return to the queue.
  const deleteReportedMonster = async (): Promise<boolean> => {
    const ok = await firestoreService.respondToViolation(mon.ownerId, moderationMonsterId(), 'delete');
    if (ok) {
      setShowViolationModal(false);
      navigate('/monsterforge/moderation');
    }
    return ok;
  };

  // helper function to convert a monster into a serializable object to write to the firestore DB
  const toPlainObject = (m: Monster): Record<string, any> => {
    const data: any = prepMonsterForSave(m);
    const cleanedData: any = ObjectHelper.removeEmpty(data);
    const sortedData = ObjectHelper.sortObjectKeys(cleanedData);
    return sortedData;
  }

  // Persist a specific monster object to the user's collection (handling image upload/delete). Takes
  // the monster explicitly (rather than reading `mon` state) so callers that just changed it can save
  // the new value without waiting for the async state update. Returns true if it actually saved
  // (false when blocked by sign-in / subscription, which surface their own modals).
  const persistMonster = async (monsterToSave: Monster): Promise<boolean> => {
    if (!(user && user.emailVerified)) {
      setShowAccount(true);
      return false;
    }
    if (isSubscriptionExpired) {
      setShowRenewalModal(true);
      return false;
    }

    // Find the existing monster in myMonsters to get the old image URL
    const existingMonster = myMonsters.find(m => m.name === monsterToSave.name);
    const oldImageUrl = existingMonster?.image?.src;

    // Handle image changes
    if (!monsterToSave.image.src && oldImageUrl && (oldImageUrl.startsWith('http://') || oldImageUrl.startsWith('https://'))) {
      // Image was cleared - delete from Storage
      try {
        console.log('Deleting cleared image from Storage...');
        await firestoreService.deleteMonsterImage(monsterToSave.name);
        console.log('Image deleted from Storage');
      } catch (error) {
        console.error('Error deleting image from Storage:', error);
      }
    } else if (monsterToSave.image.src && monsterToSave.image.src.startsWith('data:image/')) {
      // New or replacement image (data URL) - upload it (overwrites old if exists)
      try {
        console.log('Uploading image to Storage...');
        const downloadURL = await firestoreService.uploadMonsterImage(monsterToSave.name, monsterToSave.image.src);
        monsterToSave.image.src = downloadURL;
        console.log('Image uploaded to Storage:', downloadURL);
      } catch (error) {
        console.error('Error uploading image to Storage:', error);
        // Continue with save even if upload fails - will use embedded image
      }
    }
    // else: Storage URL unchanged, do nothing

    setSelectedMonsterId('user_' + monsterToSave.name);
    firestoreService.addOrUpdateMonsterToCollection(monsterToSave, toPlainObject);
    return true;
  };

  const handleMyMonsterAdd = async () => {
    const ok = await persistMonster(mon);
    if (ok) {
      // Update saved hash immediately after saving
      setSavedMonsterHash(currentMonsterHash);
    }
  }
  const handleMyMonsterRemove = () => {
    if (user && user.emailVerified) {
      setSelectedMonsterId('');
      firestoreService.deleteMonsterFromCollection(mon.name);
    } else {
      setShowAccount(true);
    }
  }
  
  useEffect(() => {
    spellsByKeyRef.current = spellsByKey;
  }, [spellsByKey]);

  const watchUserMonsters = () => {
    firestoreService.watchUserMonsters(() => spellsByKeyRef.current, (newMyMonsters: Monster[]) => setMyMonsters(newMyMonsters));
  };

  // Fetch other users' public monsters once (on login). Stored separately from myMonsters, each
  // paired with the owner's username.
  const loadCommunityMonsters = async () => {
    const rows = await firestoreService.getPublicMonsters(() => spellsByKeyRef.current, false);
    setCommunityMonsters(rows.map(r => ({ username: r.username, monster: r.monster })));
  };

  // Once the visitor loads or switches to a monster of their own choosing, the public share URL
  // (/monsterforge/mon/:username/:monstername) no longer reflects what's shown, so drop those params.
  // No-op when not on a share URL. Call this from the monster-replacement paths (selecting,
  // loading a file, accepting a generated PC/NPC) — but NOT from in-place edits via setMon.
  const clearShareUrl = () => {
    if ((username && monstername) || monsterKey) {
      navigate('/monsterforge');
    }
  };

  // Find a built-in catalog monster (by key) in the already-loaded catalog data, as a MonsterOpen5e
  // ready for Open5e.translateMonster. v1 monsters match on slug; v2 creatures match on key and are
  // converted to the v1 shape. Returns null if the catalog isn't loaded yet or the key isn't found.
  const findCatalogMonster = (key: string): MonsterOpen5e | null => {
    let open5eMons: MonsterOpen5e[] = dataModel.monsters.data.filter(m => m.slug == key);
    if (!open5eMons || !open5eMons.length) {
      const creatures: CreatureOpen5e[] = dataModel.creatures.data.filter(m => m.key == key);
      if (creatures?.length) {
        open5eMons = creatures.map(creature => CreatureOpen5e.convertToMonsterOpen5e(creature));
      }
    }
    return open5eMons && open5eMons.length === 1 ? open5eMons[0] : null;
  };

  const handleMonsterChange = async (monsterId: string | null) => {
    clearShareUrl();
    setSelectedMonsterId(monsterId || '');
    let newHash = '';
    if (monsterId) {
      console.log(`Selected monster ID: ${monsterId}`);

      // Check if trying to load a user monster with expired subscription
      if (monsterId.startsWith('user_') && isSubscriptionExpired) {
        setShowRenewalModal(true);
        return;
      }

      const doubleColumns = mon.doubleColumns;
      let newMon = null;
      // preserve the double column value
      if (monsterId.startsWith('user_')) {
        const monsterName = monsterId.substring('user_'.length);
        newMon = myMonsters.filter(m => m.name === monsterName)[0];
        if (newMon) {
          newMon = structuredClone(newMon);
          // only monsters from the myMonsters list get hashes, the others need to be added to the list before a hash matters.
          newHash = generateMonsterHash(newMon);
        }
      } else if (monsterId.startsWith('community_')) {
        // Another user's public monster (from the community list). It keeps its ownerId, so it
        // loads read-only (with the Copy / Report controls), just like opening its share link.
        // The id is 'community_<ownerId>_<name>'; the owner id (no '_') precedes the first '_', so
        // matching on both owner and name distinguishes same-named monsters from different owners.
        const rest = monsterId.substring('community_'.length);
        const sep = rest.indexOf('_');
        const ownerId = sep >= 0 ? rest.substring(0, sep) : '';
        const monsterName = sep >= 0 ? rest.substring(sep + 1) : rest;
        const found = communityMonsters.find(r => r.monster.ownerId === ownerId && r.monster.name === monsterName);
        if (found) {
          newMon = structuredClone(found.monster);
        }
      } else {
        const open5eMon = findCatalogMonster(monsterId);
        if (open5eMon) {
          newMon = Open5e.translateMonster(structuredClone(open5eMon));
          newMon.doubleColumns = doubleColumns;
        }
      }
      if (newMon) {
        Monster.validate(newMon);
        setMon(newMon);
        setMonsterVersion(0); // Reset version when loading new monster
      }
      setShowAll(false);
    } else {
      setMon(new Monster());
      setMonsterVersion(0); // Reset version
      setShowAll(true);
    }
    setSavedMonsterHash(newHash);
    setShowSearch(false);
  };

  const handleFileLoaded = (file: File | FileList): void => {
    if (file instanceof FileList) {
      return;
    }
    clearShareUrl();

    const reader = new FileReader();

    if (
      file.name.toLowerCase().endsWith('.monster') ||
      file.name.toLowerCase().endsWith('.dnd') ||
      file.name.toLowerCase().endsWith('.json')
    ) {
      reader.onload = (e) => {
        const fileContent = e?.target?.result as string;
        try {
          const jsonObject: any = JSON.parse(fileContent);
          if (file.name.toLowerCase().endsWith('.monster')) {
            const mon = TetraCube.convertLegacyMonsterToCurrent(jsonObject);
            setMon(mon);
            setMonsterVersion(0); // Reset version when loading from file
            // Don't set saved hash - loaded from file is not saved yet
            setSavedMonsterHash('');
          } else if (
            file.name.toLowerCase().endsWith('.dnd') ||
            file.name.toLowerCase().endsWith('.json')
          ) {
            const mon = Monster.convertFromJson(jsonObject);
            NpcData.updateSpellsFromKeys(mon.npcData, spellsByKey);

            // Process image locally - will be uploaded to Storage when monster is saved
            const processLoadedImage = (dataUrl: string) => {
              mon.image.src = dataUrl;
              setMon(mon);
              setMonsterVersion(0); // Reset version when loading from file
              setSavedMonsterHash('');
            };

            // Check if image exists and needs processing
            const src = mon.image.src;
            if (src && !src.startsWith('http') && src.length > MAX_IMAGE_SIZE) {
              // Needs scaling
              scaleDownImage(src, processLoadedImage);
            } else if (src && !src.startsWith('http') && !src.startsWith('data:image/webp')) {
              // Needs WebP conversion
              convertToWebP(src, processLoadedImage);
            } else {
              // No image, image is a URL image, or already a small WebP file.
              // But we still need to call setMon, setMonsterVersion & setSavedMonsterHash:
              processLoadedImage(src);
            }

          }
          setShowAll(false);
        } catch (error) {
          console.error('Error parsing JSON file:', error);
        }
      };
      reader.readAsText(file);
    } else if (file.type.startsWith('image/')) {
      reader.onload = (e) => {
        const originalDataUrl = e?.target?.result as string;

        // Store image as data URL locally - will be uploaded to Storage when monster is saved
        const processImageDataUrl = (dataUrl: string) => {
          handleUpdateMonster('image.src', dataUrl);
          resetImageSettings();
        };

        if (file.size > MAX_IMAGE_SIZE) {
          scaleDownImage(originalDataUrl, processImageDataUrl);
        } else if (!originalDataUrl.startsWith('data:image/webp')) {
          // Only convert to WebP if it's not already WebP (to avoid re-encoding degradation)
          convertToWebP(originalDataUrl, processImageDataUrl);
        } else {
          // Already WebP, below max file size, use as-is
          processImageDataUrl(originalDataUrl);
        }
      };
      reader.readAsDataURL(file);
    } else {
      // Unsupported file type
      alert(`Unsupported file type: ${file.name}\n\nSupported file types:\n• Monster files: .monster, .dnd, .json\n• Image files: .png, .jpg, .jpeg, .gif, .webp, .svg`);
    }
  };

  const openPcModal = () => {
    if (user && user.emailVerified) {
      if (isSubscriptionExpired) {
        setShowRenewalModal(true);
        return;
      }
      setPcModalOpen(true);
    } else {
      setShowAccount(true);
    }
  }

  const closePcModal = (npcMon: Monster | null) => {
    if (npcMon) {
      clearShareUrl();
      setMon(npcMon);
    }
    setPcModalOpen(false);
  };

  const resetImageSettings = () => {
    handleUpdateMonster('image.rotation', 0);
    handleUpdateMonster('image.horizontalFlip', false);
    handleUpdateMonster('image.clip', true);
  };


  // Force 'Show Empty Blocks' off whenever the monster is read-only (owned by another user, or
  // any non-Private state).
  useEffect(() => {
    if (mon.desiredVisibility !== 'Private' || (!!mon.ownerId && mon.ownerId !== user?.uid)) {
      setShowAll(false);
    }
  }, [mon.desiredVisibility, mon.ownerId, user?.uid]);

  const handleShowAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    setShowAll(currentVal => !currentVal);
  }

  const handleUseMm2024 = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    setTourState(mon.useMm2024 ? 'useLegacy' : 'useMm2024');
    handleUpdateMonster('useMm2024', !mon.useMm2024);
  }

  const handleTwoColumnsClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    handleUpdateMonster('doubleColumns', !mon.doubleColumns);
  }

  const handleOpenPrintView = (mode: PrintMode = 'append') => {
    // 'append': add to the existing print tab if it's still open; otherwise open a fresh one.
    // 'new': always open a brand-new tab. Either way printWindowRef tracks the latest print tab so a
    // following 'append' targets it. (window.closed is true once the user closes a tab.)
    const existing = printWindowRef.current;
    const reuse = mode === 'append' && !!existing && !existing.closed;
    let newWindow = reuse ? existing : window.open('', mode === 'new' ? '_blank' : 'monsterForgePrintView');
    if (!reuse) {
      printWindowRef.current = newWindow;
    }
    if (!newWindow) {
      console.error('Failed to open new window');
      return;
    }

    try {
      const content = window.document.getElementById('content-to-print')?.innerHTML || '';

      if (reuse && newWindow.document.body) {
        // Append after the monsters already in the tab, separated by a small break.
        newWindow.document.body.insertAdjacentHTML('beforeend', buildSeparatorHtml() + buildMonsterBlockHtml(content, mon.useMm2024));
      } else {
        const printHtml = buildPrintDocumentHtml(content, mon.useMm2024, collectStyleSheets());
        newWindow.document.open();
        newWindow.document.write(printHtml);
        newWindow.document.close();
      }
      newWindow.focus();
    } catch (error) {
      console.error('Error preparing print view:', error);
    }
  };

  const buyMeKoffee = (
      <a href='https://ko-fi.com/Q5Q8WUEG1' target='_blank' rel="noreferrer">
        <img height='34' src='https://storage.ko-fi.com/cdn/kofi6.png?v=3' alt='Buy Me a Coffee at ko-fi.com' />
      </a>
  );

  const prepMonsterForSave = (m: Monster): Monster => {
    const copy = structuredClone(m);
    if (copy.npcData) {
      // clear these out, so the file is smaller. We will re-get them
      // from the spells list on load from the corresponding slug lists.
      copy.npcData.spellbookSpellsArrayBySpellLevelPrimary = [];
      copy.npcData.spellbookSpellsArrayBySpellLevelSecondary = [];
      copy.npcData.spellsPreparedArrayBySpellLevelPrimary = [];
      copy.npcData.spellsPreparedArrayBySpellLevelSecondary = [];
    }
    return copy;
  }

  // The Share modal lets the owner choose Private / Shared / Public (desiredVisibility). Shared and
  // Public are just requests — the link works immediately, but community listing (Public) requires
  // moderator approval, and any outstanding report hides it. While not Private the monster is
  // read-only. Every choice persists immediately (no separate Save step): Private goes through
  // revertMonsterToPrivate (a server call — the owner can't write the moderator-only fields directly;
  // outstanding violations are left for the owner to acknowledge via resolveMyViolation). Sharing
  // (Shared/Public) goes through the setDesiredVisibility server call, which scans the saved monster
  // for profanity (rejecting the share if found), self-approves for an approver, and flips the level
  // without the client rewriting the moderator-managed approval. When coming from Private, the content
  // is saved first (kept Private) so the server scans the latest text.
  const handleVisibilitySelect = async (intent: 'Private' | 'Shared' | 'Public') => {
    // No-op if nothing is actually changing (e.g. avoids a pointless revertMonsterToPrivate call
    // when the monster is already Private).
    if (mon.desiredVisibility === intent) {
      return;
    }
    setShareError(''); // clear any prior rejection message for this fresh attempt
    // Can't request sharing while there are unresolved violations — the owner must acknowledge each
    // one in the reports view first. (Errors never count toward violationSummary.)
    if ((intent === 'Shared' || intent === 'Public') && hasOutstandingViolations(mon.violationSummary)) {
      return;
    }
    // Ignore re-entrant clicks while a previous change is still persisting (the UI is updated
    // optimistically below, which invites a quick second click that would race overlapping writes).
    if (visibilityChangingRef.current) {
      return;
    }
    visibilityChangingRef.current = true;

    try {
      if (intent === 'Private') {
        // Optimistically switch to Private right away so the UI responds instantly, then revert if the
        // server call fails. (Waiting on revertMonsterToPrivate made the UI feel hung.) The server
        // clears desiredVisibility + approval but leaves outstanding violations for the owner to
        // acknowledge via resolveMyViolation, so don't touch violationSummary.
        const prevMon = mon, prevCur = currentMonsterHash, prevSaved = savedMonsterHash;
        let updated = Monster.updateMonster(mon, 'desiredVisibility', 'Private', false);
        updated = Monster.updateMonster(updated, 'approval', null, false);
        const hash = generateMonsterHash(updated);
        setMon(updated);
        setCurrentMonsterHash(hash);
        setSavedMonsterHash(hash);
        const ok = await firestoreService.revertMonsterToPrivate(moderationMonsterId());
        if (!ok) {
          setMon(prevMon);
          setCurrentMonsterHash(prevCur);
          setSavedMonsterHash(prevSaved);
        }
      } else {
        // Sharing (Shared/Public). Route through the server gate (setDesiredVisibility), which scans
        // the SAVED monster for profanity and rejects the share if any is found (and self-approves for
        // an approver). When coming from Private the monster may have unsaved edits, so persist the
        // content first — kept Private — so the server scans the latest text and a rejected share
        // leaves a clean, saved, Private monster. The change is shown optimistically and reverted on
        // failure; the snapshot listener then mirrors any approval change the server made.
        const prevMon = mon;
        if (mon.desiredVisibility === 'Private') {
          const contentOk = await persistMonster(mon);
          if (!contentOk) {
            return; // sign-in / subscription modal already shown by persistMonster
          }
        }
        const updated = Monster.updateMonster(mon, 'desiredVisibility', intent, false);
        const hash = generateMonsterHash(updated);
        setMon(updated);
        setCurrentMonsterHash(hash);
        setSavedMonsterHash(hash);
        const res = await firestoreService.setDesiredVisibility(moderationMonsterId(), intent);
        if (!res.success) {
          // Revert to the pre-share state and surface why (e.g. profanity) in the Share modal.
          setMon(prevMon);
          const revHash = generateMonsterHash(prevMon);
          setCurrentMonsterHash(revHash);
          setSavedMonsterHash(revHash);
          setShareError(res.message || 'This monster could not be shared.');
        }
      }
    } finally {
      visibilityChangingRef.current = false;
    }
  }

  // Owner acknowledges ("resolves") a single report against their own monster. The server returns the
  // recomputed summary + error count; mirror both locally so the Share gate, the red border and the
  // View reports / Violations signals update immediately.
  const handleResolveMyViolation = async (violationId: string): Promise<boolean> => {
    const result = await firestoreService.resolveMyViolation(moderationMonsterId(), violationId);
    if (!result) {
      return false;
    }
    let updated = Monster.updateMonster(mon, 'violationSummary', result.violationSummary, false);
    updated = Monster.updateMonster(updated, 'errorReportCount', result.errorReportCount, false);
    setMon(updated);
    return true;
  };
  // Prepare monster for export - embeds Storage images as data URLs
  const prepMonsterForExport = async (): Promise<Record<string, any>> => {
    const monsterCopy = structuredClone(mon);

    // If image is a Storage URL, fetch and embed it
    if (monsterCopy.image?.src &&
        (monsterCopy.image.src.startsWith('http://') || monsterCopy.image.src.startsWith('https://'))) {
      console.log('Fetching Storage image for export...');
      const dataUrl = await fetchImageAsDataUrl(monsterCopy.image.src);
      if (dataUrl) {
        monsterCopy.image.src = dataUrl;
        console.log('Image embedded in export');
      }
    }
    setTourState('monster saved');
    return toPlainObject(monsterCopy);
  };

  // Open the search grid filtered to a single Source. The grid's Source column is document__slug
  // (the owner's username for a community monster, or the document slug for a catalog monster), and
  // the 'source:' qualifier searches that field. Clear other filters so they don't narrow results.
  const showMonstersBySource = (sourceValue: string) => {
    setSearchKeywords(`source:${sourceValue}`);
    setSearchMinCr(null);
    setSearchMaxCr(null);
    setSearchEnvironments([]);
    setShowSearch(true);
  };

  const topLeft = (<div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    color: 'white',
    padding: '10px',
    fontFamily: 'imprint MT Shadow, Arial, sans-serif',
    fontSize: '24px',
    fontWeight: '500',
    zIndex: 500,
    textShadow: '-2px 2px 2px black', // Adds the drop shadow with 2 pixels of blurring
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '-16px' }}>
      <a href="http://greengodgames.com" style={{ textDecoration: 'none', color: 'inherit' }}>
        <img src={logoTrimmedImg} alt="Green God Games" height="64px" style={{ marginRight: '8px' }} />
      </a>
      <div style={{ textDecoration: 'none', color: 'inherit' }}>
        <a href="http://greengodgames.com" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div style={{ flex: '1', textAlign: 'left', marginLeft: '-15px' }}>Green God Games</div>
        </a>
        <div style={{marginTop: '-15px'}}>
          <a style={{color: '#8888ff', textAlign: 'right', fontSize: '12px', fontWeight: '400', textShadow: 'black -1px 1px 1px' }}
             target="_" href="https://discord.gg/4dRrUpG5VJ">Join us on discord</a>
        </div>
      </div>
    </div>
  </div>);

  // The owner's own monsters that currently have outstanding reports (violations/copyright that hide
  // the monster, or data-error feedback) — their personal queue. From the live myMonsters snapshot.
  const myFlaggedMonsters = myMonsters
    .filter(m => hasOutstandingViolations(m.violationSummary) || (m.errorReportCount || 0) > 0)
    .map(m => ({ name: m.name, open: m.violationSummary?.open || 0, confirmed: m.violationSummary?.confirmed || 0, errors: m.errorReportCount || 0 }));

  const topRight = (<div style={{
    top: 0,
    textAlign: 'right',
    color: 'white',
    padding: '10px',
    fontSize: '12px',
    fontWeight: '400',
    zIndex: 500,
    textShadow: '-2px 2px 2px black', // Adds the drop shadow with 2 pixels of blurring
  }}>
    <button className="tour-button" onClick={() => setShowTourSelector(true)} id="startTour"
            title="Tours">?</button> &nbsp;
    <Login showAccount={showAccount} setShowAccount={setShowAccount} onUserChanged={onUserChanged}/>
    {userData?.canApprove && (
      <div style={{ marginTop: '4px' }}>
        <Link to="/monsterforge/moderation" style={{ color: 'white' }}>Moderation</Link>
      </div>
    )}
    {myFlaggedMonsters.length > 0 && (
      <div style={{ marginTop: '4px' }}>
        <button type="button" onClick={() => setShowMyFlaggedModal(true)}
                title="View your monsters that have been reported and need attention."
                style={{ color: 'black', fontWeight: 'bold', background: 'red', border: 'none',
                         padding: '5px', cursor: 'pointer', fontSize: 'medium', textDecoration: 'underline' }}>
          Violations ({myFlaggedMonsters.length})
        </button>
      </div>
    )}
  </div>);

  const monsterInMyMonsters = user != null && myMonsters?.filter(o => o.name === mon.name).length > 0;
  // Shared by the main "Add/Update" button and the share modal's "Update" button so both reflect
  // the same "nothing to save" state: disabled when over the monster limit, or when the current
  // monster matches what's already saved (hashes equal).
  const saveDisabled = (!monsterInMyMonsters && monsterLimit > 0 && myMonsters?.length >= monsterLimit) ||
                       (currentMonsterHash === savedMonsterHash && savedMonsterHash !== '');
  const showDownloadAllMonster = showAll && showCache && myMonsters?.length > 0 &&
                                 userData?.subscriptionTier === SubscriptionTier.LIFETIME;
  // The Private/Shared selector requires a logged-in user with a registered username (sharing
  // builds a link from that username), and that this monster already exists in their My Monsters
  // list — the shared link resolves to the saved monster of that name, so an unsaved monster has
  // nothing to share.
  const showVisibilityButton = !!userData?.username && monsterInMyMonsters &&
                               (userData?.subscriptionTier === SubscriptionTier.LIFETIME ||
                                userData?.subscriptionTier === SubscriptionTier.MASTERSMITH ||
                                userData?.subscriptionTier === SubscriptionTier.SMITH);

  // The monster is editable only when it is the owner's own 'Private' monster (desiredVisibility).
  // Any shared/public request, or another user's monster, is read-only.
  const ownedByOther = !!mon.ownerId && mon.ownerId !== user?.uid;
  // The Add/Update/Copy button. Viewing another user's monster behaves like a catalog monster: Add it
  // to your collection (always a NEW entry, so the monster-limit applies), and if you already have a
  // monster with that name it becomes 'Copy' (saved with a ' (copy)' suffix instead of overwriting).
  const overMonsterLimit = monsterLimit > 0 && (myMonsters?.length || 0) >= monsterLimit;
  const addLabel = ownedByOther ? (monsterInMyMonsters ? 'Copy' : 'Add') : (monsterInMyMonsters ? 'Update' : 'Add');
  const addDisabled = ownedByOther ? overMonsterLimit : saveDisabled;
  const addOnClick = ownedByOther ? handleAddOtherUsersMonster : handleMyMonsterAdd;
  const addTitle = ownedByOther
    ? (overMonsterLimit ? 'You have no room for additional monsters.\nPurchase or upgrade your subscription to increase your limit'
       : monsterInMyMonsters ? 'You already have a monster with this name; this will be added as a copy.'
       : 'Add this monster to your collection.')
    : ((!user || !user.emailVerified) ? 'These feature are only available when you are logged in.'
       : isSubscriptionExpired ? 'Your subscription has expired. Click to renew and continue using this feature.'
       : (currentMonsterHash === savedMonsterHash && savedMonsterHash !== '') ? 'No changes to save'
       : (!monsterInMyMonsters && myMonsters?.length >= monsterLimit) ? 'You have no room for additional monsters.\nPurchase or upgrade your subscription to increase your limit'
       : monsterInMyMonsters ? 'Update the current monster with this name in the \'My Monsters\' list'
       : 'Add this monster to the \'My Monsters\' section of the monster selector');
  // Lock editing only for YOUR OWN shared monster (you must take it Private to edit, so it gets
  // re-reviewed). Another user's monster and catalog monsters stay editable — edits are local until you
  // Add/Copy them, so there's nothing to protect (catalog monsters are Private-by-default, hence already
  // editable; this makes someone else's monster behave the same).
  const readOnly = !ownedByOther && mon.desiredVisibility !== 'Private';
  const canApprove = userData?.canApprove === true;
  const ownerUsername = reportedOwnerUsername();
  const hasViolations = hasOutstandingViolations(mon.violationSummary);
  // A sharing request awaiting moderator approval (no outstanding violations).
  const isPendingApproval = isAwaitingApproval(mon.desiredVisibility, mon.approval, mon.violationSummary);
  // An approver moderating another user's reported monster: violation-response flow + red border.
  const moderatingViolation = canApprove && ownedByOther && hasViolations;
  const ownsThisMonster = !ownedByOther && !!mon.ownerId && mon.ownerId === user?.uid;
  // A monster from the built-in catalog (selected from the data model): not user-created and not
  // yours, so it can still be reported. selectedMonsterId is the catalog slug for these; a 'user_' /
  // 'community_' id, or an empty id (a brand-new monster you're building), is excluded.
  const isCatalogMonster = !ownedByOther && !!selectedMonsterId &&
    !selectedMonsterId.startsWith('user_') && !selectedMonsterId.startsWith('community_');
  // Source shown at the bottom of the monster, as a link that opens the grid filtered to that source:
  //  - another user's monster -> their username, linking to all their public monsters
  //  - a catalog monster -> its document source, linking to all monsters from that source (by slug)
  //  - otherwise (your own / new monster) -> plain text
  const catalogSourceSlug = mon.doc_ref?.document__slug;
  let sourceNode: React.ReactNode;
  if (ownedByOther && ownerUsername) {
    sourceNode = <a href="#" onClick={(e) => { e.preventDefault(); showMonstersBySource(ownerUsername); }}
                    title={`Show all monsters shared publicly by ${ownerUsername}`}><b>{ownerUsername}</b></a>;
  } else if (isCatalogMonster && catalogSourceSlug) {
    sourceNode = <a href="#" onClick={(e) => { e.preventDefault(); showMonstersBySource(catalogSourceSlug); }}
                    title={`Show all monsters from ${mon.doc_ref.document__title}`}><b>{mon.doc_ref.document__title}</b></a>;
  } else {
    sourceNode = <b>{mon.doc_ref.document__title}</b>;
  }
  // The owner viewing their OWN monster that has outstanding violations (red border + alert styling).
  const ownHasViolations = ownsThisMonster && hasViolations;
  // Frame the monster in red whenever it is shown with outstanding violations (owner or approver).
  const showViolationBorder = moderatingViolation || ownHasViolations;

  // --- Button-area sections (see the 12-case grid). Three carved-out groups: My Monsters (Add/Update/
  // Copy + Remove, always shown); Sharing (Share + View reports, for your own monster, plus a
  // moderator's View reports on someone else's reported monster); Moderating (Report, and Approve/
  // Reject only when a moderator views a pending-approval monster).
  // For another user's monster or a catalog monster, the Share button is "view only": it just shows
  // the link (with a copy button) — no Private/Shared/Public chooser, pending message, or guidelines.
  const shareViewOnly = ownedByOther || isCatalogMonster;
  // The Share button: full chooser for your own monster (sharing tier required), or the view-only link
  // for anyone else's / a catalog monster.
  const showShareButton = (showVisibilityButton && ownsThisMonster) || shareViewOnly;
  // Owner sees View reports when their monster has outstanding violations OR data-error feedback
  // (errors don't bump violationSummary, so they're tracked separately as errorReportCount).
  const showOwnerViewReports = ownsThisMonster && (hasViolations || (mon.errorReportCount || 0) > 0);
  const showApproveReject = canApprove && ownedByOther && isPendingApproval;
  // A moderator can review the full report history (including resolved reports) on another user's
  // monster while it has outstanding violations OR is pending approval — it opens the moderator
  // reports modal. Whether any reports exist isn't known client-side without a server call, so for
  // the pending-approval case the button is simply always enabled rather than disabled-when-empty.
  const moderatorViewReports = moderatingViolation || showApproveReject;
  const showViewReports = showOwnerViewReports || moderatorViewReports;
  // Report shows for anything that isn't your own — but not alongside Approve/Reject, since rejecting
  // a pending monster is effectively the moderator's version of reporting it.
  const showReportButton = (ownedByOther || isCatalogMonster) && !showApproveReject;
  const showSharingSection = showShareButton || showViewReports;
  const showModeratingSection = showReportButton || showApproveReject;

  // The link shown in the Share modal: your own monster's share URL, another user's share URL, or a
  // catalog monster's /cat/<key> URL.
  const shareLink = isCatalogMonster
    ? `${window.location.origin}/monsterforge/cat/${encodeURIComponent(selectedMonsterId)}`
    : ownedByOther
      ? reportedMonsterUrl()
      : `${window.location.origin}/monsterforge/mon/${encodeURIComponent(userData?.username || '')}/${encodeURIComponent(mon.name)}`;
  // Caption for the view-only Share modal (other user's / catalog monster).
  const shareViewLabel = isCatalogMonster
    ? 'This is a built-in catalog monster — anyone can open this link to view it.'
    : 'Anyone with this link can view this monster.';
  // Share button labels + styling. Shared/Public both read "Shared"; the second line says how it's
  // shared. Blue with "(pending)" while awaiting approval, green once approved/shared, default while
  // Private. A catalog monster is effectively shared (always viewable), so it reads "Shared / catalog".
  const isShared = mon.desiredVisibility !== 'Private' || isCatalogMonster;
  const shareTopLabel = isShared ? 'Shared' : 'Share';
  let shareSubLabel = isCatalogMonster ? 'catalog'
                    : mon.desiredVisibility === 'Public' ? 'publicly'
                    : mon.desiredVisibility === 'Shared' ? 'by URL'
                    : 'private';
  if (mon.desiredVisibility !== 'Private' && !isCatalogMonster && isPendingApproval) {
    shareSubLabel += ' (pending)';
  }
  // View-only (catalog / another user's monster): a teal halfway between green and the normal blue
  // button. The owner's own shared monster stays blue while pending, green once approved/shared.
  const shareButtonColor = !isShared ? null
                         : shareViewOnly ? '#53a97e'
                         : (isPendingApproval ? 'blue' : 'green');

  return (
    <div className='main-content fadeInElement'>
      {topLeft}
      {!showPrivacy && topRight}
      {showPrivacy && <PrivacyNotice onClose={() => setShowPrivacy(false)} />}
      <SubscriptionRenewalModal
        isOpen={showRenewalModal}
        monsterCount={myMonsters.length}
        onRenew={() => {
          setShowRenewalModal(false);
          setShowAccount(true);
        }}
        onClose={() => setShowRenewalModal(false)}
        expirationDate={userData?.subscriptionExpirationDate?.toDate ? userData.subscriptionExpirationDate.toDate() : userData?.subscriptionExpirationDate}
      />
        {showTour && (
          <TourMain  currentStepIndex={tourStep}
                      selectedMonsterId={selectedMonsterId}
                      mon={mon}
                      openBlock={openBlock}
                      openIndex={openIndex}
                      searchOpen={searchOpen}
                      spellbookOpen={spellbookOpen}
                      showAll={showAll}
                      tourState={tourState}
                      searchMinCr={searchMinCr} searchMaxCr={searchMaxCr} searchSortBy={searchSortBy}
                      searchKeywords={searchKeywords} searchSelectedEnvironments={searchEnvironments}
                      setCurrentStepIndex={(index: number) => setTourStep(index)}
                      onClose={onCloseTour}
                      setShowAll={setShowAll}/>
          )}
        {showGridTour && (
          <TourGridSearch currentStepIndex={gridTourStep}
                          searchMinCr={searchMinCr} searchMaxCr={searchMaxCr}
                          searchKeywords={searchKeywords} searchSelectedEnvironments={searchEnvironments}
                          tourState={tourState}
                          setCurrentStepIndex={setGridTourStep}
                          onClose={(completed) => { setShowGridTour(false); if (completed) setShowTourSelector(true); else maybeShowResumeHint(); }} />
          )}
        {showMm2024Tour && (
          <TourMM2024 currentStepIndex={mm2024TourStep}
                      mon={mon} openBlock={openBlock} openIndex={openIndex} showAll={showAll}
                      setCurrentStepIndex={setMm2024TourStep}
                      onClose={(completed) => { setShowMm2024Tour(false); if (completed) setShowTourSelector(true); else maybeShowResumeHint(); }} />
          )}
        {showSpellsTour && (
          <TourSpells currentStepIndex={spellsTourStep}
                      mon={mon} openBlock={openBlock} openIndex={openIndex} showAll={showAll}
                      tourState={tourState} spellbookOpen={spellbookOpen}
                      setCurrentStepIndex={setSpellsTourStep}
                      onClose={(completed) => { setShowSpellsTour(false); if (completed) setShowTourSelector(true); else maybeShowResumeHint(); }} />
          )}
        <TourSelector isOpen={showTourSelector} onClose={() => { setShowTourSelector(false); maybeShowResumeHint(); }} onSelect={handleSelectTour} />
        <ResumeHint isOpen={showResumeHint} onClose={() => setShowResumeHint(false)} />
      {/* Drawn 3D clouds (no image). Each SVG builds organic cloud shapes from fractal noise and lights
          that noise as a 3D height-field (feDiffuseLighting) so puffs have a lit crown and a shaded
          side. feComponentTransfer shapes the cloud alpha (defined edges + a faint whiteness floor for
          contrast). Filters are static; only the CSS transform animates (drift + grow) — see .cloudSvg
          in MonsterDisplay.css. Layers fan out at evenly-spaced directions (CLOUD_LAYER_DEFS). */}
      <div className='layerImage'>
        {CLOUD_LAYER_DEFS.map(c => (
          <svg key={c.id} className='cloudSvg' xmlns='http://www.w3.org/2000/svg'
               style={{
                 ['--dx' as string]: `${c.dx}%`,
                 ['--dy' as string]: `${c.dy}%`,
                 ['--grow' as string]: c.grow,
                 animationDuration: `${c.durationS}s`,
                 animationDelay: `${c.delayS}s`,
                 opacity: Number(c.opacity),
               } as React.CSSProperties}>
            <filter id={c.id} colorInterpolationFilters='sRGB'>
              <feTurbulence type='fractalNoise' baseFrequency={c.baseFrequency} numOctaves={3} seed={c.seed}
                            stitchTiles='stitch' result='noise' />
              {/* Cloud alpha: steepen the noise into defined cloud masses with edges, keeping a small
                  floor (first value) so there's always a little whiteness everywhere for contrast. */}
              <feComponentTransfer in='noise' result='mask'>
                <feFuncA type='table' tableValues='0.08 0.08 0.14 0.4 0.7 0.92 1 1' />
              </feComponentTransfer>
              {/* Light the same noise as a 3D surface: lit crowns, shadowed sides. The tone (white or
                  grey) varies per layer so overlapping clouds stay distinct and gain depth. */}
              <feDiffuseLighting in='noise' surfaceScale={Number(c.surfaceScale)} diffuseConstant={1.2}
                                 lightingColor={c.tone} result='lit'>
                <feDistantLight azimuth={235} elevation={60} />
              </feDiffuseLighting>
              {/* Keep the shaded white only within the cloud shapes. */}
              <feComposite in='lit' in2='mask' operator='in' />
            </filter>
            <rect width='100%' height='100%' filter={`url(#${c.id})`} />
          </svg>
        ))}
      </div>
      <br />
      <div className={ 'monster-container' + (mon.useMm2024 ? ' mm2024' : '')}>
        <div style={{
          fontSize: '48px', textAlign: 'center', position: 'relative', color: '#224444',
          fontFamily: 'Gabriola', fontWeight: '800', textShadow: '0px 0px 3px white'
        }}><span id="mainTitle">Monster Forge</span></div>
        <div className="select-container" style={{ width: '100%' }}>
          <span style={{fontSize:"small"}}>Select<br/>monster:</span>
          <MonsterSelector onMonsterChange={handleMonsterChange} selectedMonsterId={selectedMonsterId} dataModel={dataModel} myMonsters={myMonsters} communityMonsters={communityMonsters} />
          {
            <MonsterSearch isOpen={showSearch} setIsOpen={(isOpen) => {setShowSearch(isOpen); setTourState(isOpen ? 'monsterGridOpen' : 'monsterGridClosed')} } handleMonsterChange={handleMonsterChange}
              minCr={searchMinCr} maxCr={searchMaxCr} keywords={searchKeywords} selectedEnvironments={searchEnvironments} dataModel={dataModel}
              pageSize={searchPageSize} currentPage={searchCurrentPage} sortBy={searchSortBy}
              onUpdateMinCr={setSearchMinCr} onUpdateMaxCr={setSearchMaxCr} onUpdateKeywords={setSearchKeywords} onUpdateSelectedEnvironments={setSearchEnvironments}
              onUpdatePageSize={setSearchPageSize} onUpdateCurrentPage={setSearchCurrentPage} onUpdateSort={setSearchSortBy} myMonsters={myMonsters} communityMonsters={communityMonsters}
              onStartTour={() => { setGridTourStep(0); setShowGridTour(true); }} tourActive={showGridTour} />
          }
          <table title={(user && user.emailVerified) ? '' : 'These feature are only available when you are logged in.'} style={{border: '0px solid black'}}>
            <tbody>
            <tr>
              <td style={{fontSize: 'smaller'}}>My&nbsp;monsters:<br/>
              ({myMonsters?.length || 0 } / {monsterLimit})</td>
              <td id='myMonsterSection'>
                {/* Add/Update/Copy: adds the current monster to your collection. Viewing another user's
                    monster works like a catalog one — Add it (or Copy, renamed, on a name collision).
                    Remove only ever acts on your OWN collection, so it stays disabled for others'. */}
                <button onClick={addOnClick} className='myMonsterButton'
                        disabled={addDisabled}
                        title={addTitle} >
                  <img src={saveImg} alt="{name}" width='16px' />
                  {addLabel}
                </button>
                <button onClick={handleMyMonsterRemove} className='myMonsterButton' disabled={!monsterInMyMonsters || ownedByOther}
                      title={ownedByOther ? 'This is another user\'s monster.' :
                                       (!user || !user.emailVerified) ? 'These feature are only available when you are logged in.' :
                                       !monsterInMyMonsters ? 'Monster not in DB' :
                                                              'Removes the current monster with this name from the \'My Monsters\' list'}>
                  <img src={deleteImg} alt="{name}" width='16px' /> Remove
                </button>
                {showDownloadAllMonster && <DownloadAllMonstersButton monsters={myMonsters} prepMonsterForSave={prepMonsterForSave}/>}
                {showDownloadAllMonster && <ExportAllToVttButton monsters={myMonsters}/>}
              </td>
              {/* SHARING section: your own monster's Share + View reports (and a moderator's View
                  reports on someone else's reported monster). */}
              {showSharingSection && (
                <td id='sharingSection' style={{ paddingLeft: '8px' }}>
                  {showShareButton && mon &&
                    <button className='myMonsterButton' onClick={() => { setShareError(''); setShowShareModal(true); }}
                            title="Choose who can see this monster (Private, Shared, or Public)."
                            style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                     height: 'auto', minHeight: '26px', gap: 0, lineHeight: 1.1, paddingTop: '2px', paddingBottom: '2px',
                                     ...(shareButtonColor ? { backgroundColor: shareButtonColor, color: 'white' } : {}) }}>
                      <span>{shareTopLabel}</span>
                      <i style={{ fontWeight: 'normal', fontSize: 'xx-small' }}>{shareSubLabel}</i>
                    </button>
                  }
                  {showViewReports && mon &&
                    <button className='myMonsterButton'
                            onClick={() => moderatorViewReports ? setShowViolationModal(true) : setShowMyViolationsModal(true)}
                            title="View the report(s) filed against this monster."
                            style={{ backgroundColor: '#d33', color: 'white', fontSize: '12px' }}>
                      View reports
                    </button>
                  }
                </td>
              )}
              {/* MODERATING section: Report (anything that isn't your own), plus Approve/Reject when a
                  moderator views a monster awaiting approval. Dismiss/confirm/delete of an actual
                  violation lives inside the View reports modal (above), not here. */}
              {showModeratingSection && (
                <td id='moderatingSection' style={{ paddingLeft: '8px' }}>
                  {showReportButton && mon &&
                    <button onClick={() => setShowReportModal(true)} className='myMonsterButton'
                            title="Report this monster (inappropriate content, copyright, or incorrect data).">
                      🚩 Report
                    </button>
                  }
                  {showApproveReject && mon &&
                    <>
                      <button onClick={handleApprove} className='myMonsterButton' disabled={moderationBusy}
                              title="Approve this monster (grants the level it requested).">
                        {moderationBusy ? '✓ Approving…' : '✓ Approve'}
                      </button>
                      <button onClick={() => setShowRejectModal(true)} className='myMonsterButton' disabled={moderationBusy}
                              title="Reject this monster and return it to its owner with a reason.">
                        ✗ Reject
                      </button>
                    </>
                  }
                </td>
              )}
            </tr>
            </tbody>
          </table>
        </div>
        <div id="content-to-print" style={{textAlign: 'center'}}>
          {/* Border lives on an inline-block wrapper so it hugs the monster's actual width (which can
              exceed the container) rather than the full-width print container, whose right edge would
              otherwise fall behind a wider monster. */}
          <div style={showViolationBorder
                 ? { display: 'inline-block', border: '6px solid red', borderRadius: '8px', padding: '6px' }
                 : undefined}>
          <MonsterDisplay mon={mon}
            handleUpdateMonster={handleUpdateMonster}
            handleMonsterMutated={handleMonsterMutated}
            onFileDropped={handleFileLoaded}
            onClosePopups={() => setShowSearch(false)}
            exportImageDivSource={exportImageDivSource}
            dataModel={dataModel}
            readOnly={readOnly}
            showAll={showAll}
            openBlock={openBlock} setOpenBlock={setOpenBlock}
            openIndex={openIndex} setOpenIndex={setOpenIndex}
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            spellbookOpen={spellbookOpen}
            setSpellbookOpen={setSpellbookOpen}
            onRefreshTour={setTourState}
             />
          </div>
        </div>
        <div style={{textAlign: 'center'}}>
          <div style={{background: 'lightgrey', fontSize: 'small', maxWidth: mon.doubleColumns ? '800px' : '400px', minWidth: '400px', width: '100%', display: 'inline-block'}}>
            {mon.doubleColumns && (
              <div className="container-horizontal">
                <div className="container-horizontal-left"><b>Meta information:</b></div>
                <div className="container-horizontal-center">Source: {sourceNode}</div>
                <div className="container-horizontal-right">ID: <b>{mon.doc_ref.slug}</b></div>
              </div>)}
            {!mon.doubleColumns && (<>
                <div className="container-horizontal">
                  <div className="container-horizontal-left"><b>Meta information:</b></div>
                  <div className="container-horizontal-right">ID: <b>{mon.doc_ref.slug}</b></div>
                </div>
                <div className="container-horizontal">
                  <div className="container-horizontal-left">Source: {sourceNode}</div>
                  <div className="container-horizontal-right">License: <a href={mon.doc_ref.document__license_url} target="_blank">{mon.doc_ref.document__license_url}</a></div>
                  </div>
              </>)}
            <div className="container-horizontal">
              <div className="container-horizontal-left">
                <PropertySet className='environments' noWrap columns={3} isOpen={envOpen} setIsOpen={setEnvOpen} alwaysShow={true}
                              name="Environments" options={environments} values={mon.environments}
                              onSave={(vals: string) => handleUpdateMonster("environments", vals.split(/, ?/))}
                              onClickOutside={() => setEnvOpen(false)}/>
              </div>
              {mon.doubleColumns && <div className="container-horizontal-right">License: <a href={mon.doc_ref.document__license_url} target="_blank">{mon.doc_ref.document__license_url}</a></div>}
            </div>
          </div>
        </div>
        <div>
          <div style={{textAlign:'center'}}>
            <span id="layoutControls">
              <span>
                <input onChange={handleTwoColumnsClick} id="twoCols" type="checkbox" checked={mon?.doubleColumns ?? true} disabled={readOnly} readOnly />
                <label htmlFor="twoCols">Two Columns</label>
              </span>
              &nbsp; &nbsp;
              <span style={{ textWrap: 'nowrap', display: mon.doubleColumns ? '' : 'none' }}>
                Move Items between columns:
                <button className="move-column-button" disabled={readOnly} onClick={() => handleUpdateMonster('splitPoint', Math.max(0, (mon.splitPoint + 1)))} title='Move the top item from the right column to the bottom of the left column'>⮰</button>
                <button className="move-column-button" disabled={readOnly} onClick={() => handleUpdateMonster('splitPoint', Math.max(0, (mon.splitPoint - 1)))} title='Move the bottom item from the left column to the top of the right column'>⮳</button>
              </span>
            </span>
            &nbsp; &nbsp;
            <span className='no-wrap' id="bottomEdgeControls">bottom edge:
              <button className="move-column-button" disabled={readOnly} onClick={() => handleUpdateMonster('extraWhitespace', Math.max(0, (mon.extraWhitespace - 1)))} title='Reduce the whitespace at the bottom of the columns'>⏶</button>
              <button className="move-column-button" disabled={readOnly} onClick={() => handleUpdateMonster('extraWhitespace', Math.max(0, (mon.extraWhitespace + 1)))} title='Increase the whitespace at the bottom of the columns'>⏷</button>
            </span>
          </div>
          <div className='container-horizontal'>
            <span className='container-horizontal-left'>
              <span id="showEmptyBlocks" >
                <input onChange={handleShowAllClick} id="showAll" type="checkbox" checked={showAll} disabled={readOnly} readOnly />
                <label htmlFor="showAll">Show Empty Blocks</label>
              </span>
            </span>
            <span className='container-horizontal-right'>
              <span id="showUseMm2024">
                <input onChange={handleUseMm2024} id="useMm2024" type="checkbox" checked={mon.useMm2024} disabled={readOnly} readOnly />
                <label htmlFor="useMm2024">Use MM2024 style</label>
                <button type="button" onClick={() => { setMm2024TourStep(0); setShowMm2024Tour(true); }} id="mm2024TourButton"
                        title="Take a tour of the 2024 Monster Manual style"
                        className="tour-button" style={{ marginLeft: '8px', height: '16px', width: '16px' }}>?</button>
              </span>
            </span>
          </div>
        </div>

        <div id='SaveMonsterSection'>
          <DownloadJsonButton getData={prepMonsterForExport} filename={mon.name + '.json'} />
          <DownloadVttFormatButton getData={() => structuredClone(mon)} filename={mon.name + '.mod'} />
          <DivToImage targetRef={exportImageDivSource} filename={mon.name + '.png'} />
          <PrintViewButton onPrint={handleOpenPrintView} />
        </div>
        <span id='LoadMonsterSection'><DataLoader name='Load Monster' fileTypes='.monster, .dnd, .json' onFileLoad={handleFileLoaded} title='Import monster from a .monster/.dndo.json file'/></span>
        <DataLoader name='Load Image' fileTypes='image/*' onFileLoad={handleFileLoaded} disabled={readOnly} title='Add (or replace) an image of this monster'/>
        <span className='no-wrap'>(or drag either file type onto page)</span>

        <div style={{ display: mon.image?.src ? 'block' : 'none' }} id='imageControls'>
          <span className='no-wrap'>
            <button onClick={() => handleUpdateMonster('image.src', null)} disabled={readOnly} title='Remove the current image from the monster'>Clear Image</button>
            Opacity: <input type="range" min="0" max="100" value={(mon?.image?.opacity || 1) * 100} style={{ width: '8%' }} disabled={readOnly}
              onChange={(event) => handleUpdateMonster('image.opacity', parseFloat(event.target.value) / 100.0)} />
          </span>
          <span className='no-wrap'>
            <input id='hflip' type="checkbox" checked={mon?.image?.horizontalFlip || false} disabled={readOnly}
              onChange={(event) => handleUpdateMonster('image.horizontalFlip', event.target.checked)} />
            <label htmlFor='hflip'>Horizontal Flip</label>
            <input id='imgClip' type="checkbox" checked={mon?.image?.clip || false} disabled={readOnly}
              onChange={(event) => handleUpdateMonster('image.clip', event.target.checked)} />
            <label htmlFor='imgClip'>Clip</label>
          </span>
        </div>
        <div>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px'}}>
            <table id="monster-alias-table">
              <tbody>
                <tr>
                  <th colSpan={2}>Short / Plural Names:</th>
                  <td rowSpan={3} id="openCreatePcNPC">
                    <button onClick={openPcModal} className='npcButton' style={{marginLeft: "10px", marginTop: "10px"}}
                            title={(user && user.emailVerified) ? 'Create a PC or an NPC using our characer creation wizard.' : isSubscriptionExpired ? 'Your subscription has expired. Click to renew and continue using this feature.' : 'You must be logged in to use this feature.'}
                            disabled={!user || !user.emailVerified || isSubscriptionExpired}>
                      <img src={pnNpcImg} alt="{name}" width='16px' /> Create<br/>PC/NPC
                    </button>
                  </td>
                </tr>
                <tr>
                  <td style={{textAlign: 'right'}}>Short ([MON]):</td>
                  <td>
                    <input type="text" value={mon?.shortName || ''} disabled={readOnly} onChange={(event) => handleUpdateMonster('shortName', event.target.value)} id="short-name-selector" />
                  </td>
                </tr>
                <tr>
                  <td style={{textAlign: 'right'}}>Plural ([MONS]):</td>
                  <td>
                    <input type="text" value={mon?.pluralName || ''} disabled={readOnly} onChange={(event) => handleUpdateMonster('pluralName', event.target.value)} id="plural-name-selector" />
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{textAlign: 'center', background: '#CCCCCC80', border: '1px solid black', padding: '2px 10px'}}>
              Have feedback, comments, bugs or suggestions?<br />
              I'd love to hear it! <a href="mailto:postrowski@gmail.com?subject=GGG Stats Block" target="_blank" rel="noreferrer">Send me an email</a><br />
            <div>Or, if you love it, send me a tip: {buyMeKoffee}</div>
            </div>
          </div>
          <div>
            <MessageModal isOpen={isWarnMessageOpen}
              onClose={(showInFuture: boolean, accept: boolean) => {
                setWarnMessageOpen(false);
                if (accept) {
                  setShowWarnMessage(showInFuture);
                  setPcModalOpen(true);
                }
              }} />
            <CharacterModal isOpen={isPcModalOpen} onClose={closePcModal} mon={mon} dataModel={dataModel} />
            <ReportMonsterModal isOpen={showReportModal} url={reportedMonsterUrl()} hasImage={!!mon.image?.src}
              errorOnly={isCatalogMonster}
              presetName={userData?.displayName || userData?.username || user?.displayName || ''}
              presetEmail={user?.email || ''}
              lockIdentity={!!user}
              onClose={() => setShowReportModal(false)} onSubmit={submitReport} />
            <RejectMonsterModal isOpen={showRejectModal}
              onClose={() => setShowRejectModal(false)} onSubmit={submitReject} />
            <ViolationResponseModal isOpen={showViolationModal}
              loadReports={() => firestoreService.getMonsterReports(mon.ownerId, moderationMonsterId())}
              onClose={() => setShowViolationModal(false)}
              onDismissReport={dismissOneReport} onDeleteMonster={deleteReportedMonster} />
            <MyViolationsModal isOpen={showMyViolationsModal} onClose={() => setShowMyViolationsModal(false)}
              loadViolations={() => firestoreService.getMyMonsterViolations(moderationMonsterId())}
              onResolve={handleResolveMyViolation} />
            <MyFlaggedMonstersModal isOpen={showMyFlaggedModal} onClose={() => setShowMyFlaggedModal(false)}
              monsters={myFlaggedMonsters}
              onSelectMonster={(name) => { setShowMyFlaggedModal(false); handleMonsterChange('user_' + name); }} />
            <ShareMonsterModal isOpen={showShareModal} url={shareLink}
              desiredVisibility={mon.desiredVisibility} approval={mon.approval} violationSummary={mon.violationSummary}
              error={shareError} viewOnly={shareViewOnly} viewLabel={shareViewLabel}
              onClose={() => { setShareError(''); setShowShareModal(false); }} onSelectVisibility={handleVisibilitySelect}
              onViewReports={() => { setShowShareModal(false); setShowMyViolationsModal(true); }} />
            <MonsterUnavailableModal isOpen={showUnavailableModal} username={username} monsterName={monstername}
              onClose={() => {
                setShowUnavailableModal(false);
                // Drop the dead :username/:monstername from the URL so a refresh/share doesn't
                // re-trigger the unavailable monster; the default monster is already loaded.
                if (username && monstername) {
                  navigate('/monsterforge');
                }
              }} />
            {(showAll || readOnly) && !showCache && <button className='cacheButton' onClick={() => setShowCache(true)}>Show Cache</button>}
            {(showAll || readOnly) && showCache && <button className='cacheButton' onClick={() => setShowCache(false)}>Hide Cache</button>}
            {(showAll || readOnly) && showCache && <CacheBlock show={true} showClearButton={true} dataModel={dataModel} />}
          </div>
          <div style={{ paddingBottom: '130px' }}>&nbsp;</div>
        </div>
      </div>
      <div style={{ color: 'white', textAlign: 'right', textShadow: '1px 1px 2px black' }} title="This application includes material from the System Reference Document 5.1 (SRD 5.1), published by Wizards of the Coast under the Creative Commons Attribution 4.0 International License. Content is provided via Open5e.com and is not published, owned, or endorsed by Wizards of the Coast or Green God Games. SRD 5.1 is licensed under CC BY 4.0, and the full text of the license can be found at https://creativecommons.org/licenses/by/4.0/.”">Content provided by <a style={{ color: '#BBBBFF' }} target="_blank" href="https://open5e.com/" rel="noreferrer">open5e.com</a>, under the <a style={{ color: '#BBBBFF' }} target="_blank" href="https://creativecommons.org/licenses/by/4.0/." rel="noreferrer">Creative Commons Attribution 4.0 License</a></div>
      <div style={{ color: 'white', textAlign: 'right', textShadow: '1px 1px 2px black' }}>Copyright © 2025 Green God Games, LLC. All Rights Reserved.</div>
    </div>
  );
};

export default MonsterContainer;
